import { Query } from '@cubejs-client/core';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { parseNestedJSONInDataItem } from '../../impl/cube/cubeUtils';
import {
  CubePaginationOptions,
  useCubeQueryChunkLoadingSync,
} from '../../impl/cube/hooks/useCubeQueryChunkLoadingSync';

/**
 * Custom Cube.js query hook that accepts a query, schema, and other parameters to fetch and validate data from Cube.js.
 *
 * @template T - The interface/type of the individual data item, extending Yup.AnyObject.
 * @param {Query} query - The Cube.js query to be executed.
 * @param {boolean} skip - Whether to skip the query execution or not.
 * @param {Yup.ObjectSchema<T, Yup.AnyObject>} [schema] - Optional Yup validation schema for validating individual data items.
 *  @param {string} parseJSONString - A boolean to indicate whether to parse the raw data from a JSON string or not.
 * @param {CubePaginationOptions} [paginationOptions] - Optional pagination options for the query.
 * @returns {{
 *  rawData: any[],
 *  validatedData: T[] | null,
 *  isLoading: boolean,
 *  progress: number,
 *  error: any,
 * }} - An object containing the raw data, validated data, loading status, progress, and error.
 */
export function useCubeQueryCustom<T extends Yup.AnyObject = Yup.AnyObject>(
  query: Query,
  skip: boolean,
  schema?: Yup.ObjectSchema<T, Yup.AnyObject>,
  parseJSONString = false,
  paginationOptions?: CubePaginationOptions
) {
  const { resultSet, isLoading, error } = useCubeQueryChunkLoadingSync(query, {
    skip: skip,
    ...paginationOptions,
  });

  // extract the raw points from the result set
  const rawData = useMemo(() => resultSet?.rawData(), [resultSet]);

  // If a yup validation schema is provided, validate the data and type it to the provided interface
  const validatedData = useMemo(() => {
    if (schema && rawData) {
      const validated: T[] = [];
      //  eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errors: any[] = [];

      rawData.forEach((dataItem) => {
        let dataItemCopy = dataItem;
        // If the raw data is a JSON string, parse it
        if (parseJSONString && dataItem) {
          dataItemCopy = parseNestedJSONInDataItem(dataItem);
        }

        try {
          schema.validateSync(dataItemCopy, { abortEarly: false });
          validated.push(schema.cast(dataItemCopy) as unknown as T);
        } catch (validationError) {
          console.error(
            'Validation Error for data item:',
            dataItemCopy,
            'Error:',
            validationError
          );
          errors.push(validationError);
        }
      });

      if (errors.length > 0) {
        console.error('Validation Errors:', errors);
      }

      return validated;
    }
    return null;
  }, [schema, rawData]);

  if (error) {
    console.log('Cube Query Error', error);
    console.log('Attempted Query', query);
    console.log('Cube Query Result Set', resultSet);
    console.trace();
  }

  return { rawData, validatedData, isLoading, error };
}
